import { createContext, useContext, type ReactNode } from 'react';

export interface AppShellContextValue {
  /** Desktop nav is collapsed to icons. */
  navCollapsed: boolean;
  setNavCollapsed: (collapsed: boolean) => void;
  /** Collapses/expands on desktop, opens/closes the off-canvas nav on small screens. */
  toggleNav: () => void;
  /** Off-canvas nav (small screens) is open. */
  navOpen: boolean;
  openNav: () => void;
  closeNav: () => void;
  /** Viewport is below the responsive breakpoint. */
  isMobile: boolean;
  inspectorOpen: boolean;
  /** Opens the inspector, optionally replacing its content. */
  openInspector: (content?: ReactNode) => void;
  closeInspector: () => void;
  /** Replaces the inspector content pushed by screens (null restores the `inspector` slot). */
  setInspector: (content: ReactNode | null) => void;
  /** Content pushed through `setInspector` / `openInspector`. */
  inspectorContent: ReactNode | null;
  /**
   * The overlay layer covering the content region, into which `InspectorPanel` portals.
   *
   * It is a sibling of `main`, never an ancestor, so a screen that renders an inspector keeps its own
   * subtree — and therefore its state — exactly where it was.
   */
  inspectorHost: HTMLElement | null;
}

export const AppShellContext = createContext<AppShellContextValue | null>(null);

/** Returns the AppShell context or null when rendered outside an AppShell. */
export function useOptionalAppShell(): AppShellContextValue | null {
  return useContext(AppShellContext);
}

/** Access AppShell state (nav collapse, inspector). Throws outside an AppShell. */
export function useAppShell(): AppShellContextValue {
  const ctx = useContext(AppShellContext);
  if (!ctx) throw new Error('useAppShell must be used within <AppShell>');
  return ctx;
}
